<template>
  <section id="contact" class="py-20 px-6 bg-gradient-to-br from-soft-white to-lavender-tint/20" ref="sectionRef">
    <div class="container mx-auto max-w-7xl">
      <!-- Section Header -->
      <div
        class="text-center mb-16"
        v-motion
        :initial="{ opacity: 0, y: 30 }"
        :visible-once="{ opacity: 1, y: 0 }"
        :duration="800"
      >
        <h2 class="text-4xl md:text-5xl font-poppins font-semibold text-dark-gray mb-6">
          Get in <span class="text-light-purple">Touch</span>
        </h2>
        <div class="w-24 h-1 bg-light-purple mx-auto rounded-full mb-8"></div>
        <p class="text-lg text-dark-gray/80 max-w-3xl mx-auto leading-relaxed">
          Corporates, labs, and partners — request a camp or onsite phlebotomy team. Individuals can also ask about home collection.
        </p>
      </div>

      <div class="grid lg:grid-cols-2 gap-12">
        <!-- Contact Information -->
        <div
          v-motion
          :initial="{ opacity: 0, x: -50 }"
          :visible-once="{ opacity: 1, x: 0 }"
          :duration="800"
          :delay="200"
        >
          <h3 class="text-2xl font-poppins font-semibold text-dark-gray mb-8">
            Let's Start a Conversation
          </h3>
          
          <div class="space-y-6 mb-8">
            <div
              v-for="(item, index) in contactInfo"
              :key="index"
              class="flex items-start space-x-4 card-neumorphic cursor-pointer"
              v-motion
              :initial="{ opacity: 0, y: 20 }"
              :visible-once="{ opacity: 1, y: 0 }"
              :duration="500"
              :delay="400 + index * 100"
              @mouseenter="onContactHover"
              @mouseleave="onContactLeave"
            >
              <div class="w-12 h-12 rounded-full bg-gradient-neumorphic flex items-center justify-center shadow-soft flex-shrink-0">
                <component :is="item.icon" :size="20" class="text-light-purple" />
              </div>
              <div>
                <h4 class="font-semibold text-dark-gray mb-1">{{ item.title }}</h4>
                <p class="text-light-purple font-medium">{{ item.info }}</p>
                <p class="text-dark-gray/60 text-sm">{{ item.subInfo }}</p>
              </div>
            </div>
          </div>

          <!-- Additional Info -->
          <div
            class="card-neumorphic bg-gradient-to-br from-light-purple/5 to-light-purple/10"
            v-motion
            :initial="{ opacity: 0, y: 20 }"
            :visible-once="{ opacity: 1, y: 0 }"
            :duration="500"
            :delay="700"
          >
            <h4 class="font-semibold text-dark-gray mb-3">Why Choose Homvisit?</h4>
            <ul class="space-y-2 text-dark-gray/70">
              <li
                v-for="(feature, index) in features"
                :key="index"
                class="flex items-center space-x-2"
              >
                <CheckCircle :size="16" class="text-light-purple flex-shrink-0" />
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Contact Form -->
        <div
          v-motion
          :initial="{ opacity: 0, x: 50 }"
          :visible-once="{ opacity: 1, x: 0 }"
          :duration="800"
          :delay="400"
        >
          <div class="card-neumorphic relative overflow-hidden">
            <!-- Success Overlay -->
            <div
              v-if="isSubmitted"
              class="absolute inset-0 bg-light-purple/90 flex items-center justify-center z-20 transition-all duration-300"
              style="border-radius: 24px"
              v-motion
              :initial="{ opacity: 0, scale: 0.8 }"
              :enter="{ opacity: 1, scale: 1 }"
              :duration="300"
            >
              <div class="text-center text-white">
                <CheckCircle :size="64" class="mx-auto mb-4" />
                <h3 class="text-2xl font-semibold mb-2">Request Sent Successfully!</h3>
                <p>We'll get back to you within 24 hours.</p>
              </div>
            </div>

            <h3 class="text-2xl font-poppins font-semibold text-dark-gray mb-6">
              Request a Service
            </h3>

            <form @submit.prevent="handleSubmit" class="space-y-6">
              <div class="grid md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-dark-gray/70 font-medium mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    v-model="formData.name"
                    class="input-neumorphic"
                    placeholder="Enter your name"
                    required
                  />
                </div>
                <div>
                  <label class="block text-dark-gray/70 font-medium mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    v-model="formData.organization"
                    class="input-neumorphic"
                    placeholder="Company/Organization"
                  />
                </div>
              </div>

              <div class="grid md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-dark-gray/70 font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    v-model="formData.email"
                    class="input-neumorphic"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label class="block text-dark-gray/70 font-medium mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    v-model="formData.phone"
                    class="input-neumorphic"
                    placeholder="+91 98765 43210"
                    required
                  />
                </div>
              </div>

              <div>
                <label class="block text-dark-gray/70 font-medium mb-2">
                  Service Type *
                </label>
                <select
                  v-model="formData.service"
                  class="input-neumorphic"
                  required
                >
                  <option value="">Select a service</option>
                  <option value="corporate-camp">Corporate Medical Camp</option>
                  <option value="onsite-phlebotomy">Onsite Phlebotomy for Companies</option>
                  <option value="phlebo-staffing">Phlebotomist Staffing</option>
                  <option value="home-collection">Home Blood Collection (secondary)</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label class="block text-dark-gray/70 font-medium mb-2">
                  Message
                </label>
                <textarea
                  v-model="formData.message"
                  rows="4"
                  class="input-neumorphic resize-none"
                  placeholder="Tell us about your requirements..."
                ></textarea>
              </div>

              <button
                type="submit"
                class="w-full btn-neumorphic text-light-purple hover:text-white hover:bg-light-purple group relative overflow-hidden"
                :disabled="isSubmitted"
                @mouseenter="onButtonHover"
                @mouseleave="onButtonLeave"
              >
                <span class="relative z-10 flex items-center justify-center space-x-2">
                  <Send :size="20" />
                  <span>Send Request</span>
                </span>
                <div
                  class="absolute inset-0 bg-light-purple transition-all duration-300 opacity-0 group-hover:opacity-100"
                  style="border-radius: 24px"
                />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, reactive } from 'vue'
import { Send, CheckCircle, Phone, Mail, MapPin } from 'lucide-vue-next'
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  LOCATION_LINE,
} from '../config/contact'
import { submitForm } from '../services/submitForm'

export default {
  name: 'ContactSection',
  components: {
    Send,
    CheckCircle,
    Phone,
    Mail,
    MapPin
  },
  setup() {
    const sectionRef = ref(null)
    const isSubmitted = ref(false)

    const formData = reactive({
      name: '',
      organization: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    })

    const contactInfo = [
      {
        icon: Phone,
        title: 'Call Us',
        info: CONTACT_PHONE_DISPLAY,
        subInfo: 'Mon-Sat 9:00 AM - 6:00 PM'
      },
      {
        icon: Mail,
        title: 'Email Us',
        info: CONTACT_EMAIL,
        subInfo: 'We reply within 24 hours'
      },
      {
        icon: MapPin,
        title: 'Visit Us',
        info: 'Homvisit',
        subInfo: LOCATION_LINE
      }
    ]

    const features = [
      'Corporate health & medical camps across Kerala',
      'Onsite phlebotomy teams for companies',
      'Always-open phlebotomist network',
      'Home collection available on request'
    ]

    const handleSubmit = async () => {
      try {
        await submitForm({
          subject: `Contact — ${formData.service || 'general'}`,
          formName: 'Contact Form',
          data: {
            name: formData.name,
            organization: formData.organization,
            email: formData.email,
            phone: formData.phone,
            service: formData.service,
            message: formData.message,
            intent: 'contact_form',
          },
        })
        
        isSubmitted.value = true
        setTimeout(() => {
          isSubmitted.value = false
          Object.keys(formData).forEach(key => {
            formData[key] = ''
          })
        }, 3000)
      } catch (error) {
        console.error('Error sending contact form:', error)
        alert(
          `Could not send. WhatsApp ${CONTACT_PHONE_DISPLAY} or email ${CONTACT_EMAIL}.`
        )
      }
    }

    const onContactHover = (e) => {
      e.currentTarget.style.transform = 'scale(1.02)'
    }

    const onContactLeave = (e) => {
      e.currentTarget.style.transform = 'scale(1)'
    }

    const onButtonHover = (e) => {
      e.target.style.transform = 'scale(1.02)'
    }

    const onButtonLeave = (e) => {
      e.target.style.transform = 'scale(1)'
    }

    return {
      sectionRef,
      formData,
      isSubmitted,
      contactInfo,
      features,
      handleSubmit,
      onContactHover,
      onContactLeave,
      onButtonHover,
      onButtonLeave
    }
  }
}
</script>
